/**
 * People → Talent Acquisition → TA Metrics: dashboard de métricas TA (documento
 * "Agente TA Metrics", Ignacio Bresso), Hiring Plan, la casa del asistente y,
 * para admins, actividad y administración. Mientras la página está montada,
 * supportIA (el bot del costado) pasa a modo Agente TA Metrics — mismo mecanismo
 * que el Hub de Partnerships. Tabs por query param (?tab=).
 * El Hiring Manager Intake tiene su propia entrada en el menú y su página
 * (TaIntakePage, /people/talent-acquisition/intake); los links viejos
 * (?tab=intake / ?tab=solicitudes) se redirigen ahí.
 * 28/09/2026: "Asistente" (la casa del Agente TA Metrics; el chat sigue en el bot
 * del costado) y "Admin" (reemplaza "Permisos": permisos, hiring managers, prompts
 * del bot, reglas y fotos en subtabs propias; ?tab=permisos redirige a Admin).
 */

import { currentLocale } from '@/lib/locale';
import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { useRegisterTaContext, useTaAccess, useTaDashboard } from '@/hooks/useTalentAcquisition';
import { TaDashboard } from '@/components/talent-acquisition/TaDashboard';
import { TaAdminTab } from '@/components/talent-acquisition/TaAdminTab';
import { TaAssistantTab } from '@/components/talent-acquisition/TaAssistantTab';
import { TaActivityTab } from '@/components/talent-acquisition/TaActivityTab';
import { TaHiringPlanTab } from '@/components/talent-acquisition/TaHiringPlanTab';
import { TaJdLibraryTab } from '@/components/talent-acquisition/TaJdLibraryTab';

export function TalentAcquisitionPage() {
  const { t } = useTranslation();
  // El bot del costado: supportIA responde solo sobre la base de TA en esta sección
  useRegisterTaContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: access } = useTaAccess();
  const canAdmin = access?.canAdmin ?? false;
  const canView = access?.canView ?? false;
  // "permisos" (links viejos) ahora es una sección del Admin (28/09)
  const rawTab = searchParams.get('tab') === 'permisos' ? 'admin' : searchParams.get('tab');
  const tab = rawTab === 'hiring' || rawTab === 'asistente' || rawTab === 'jds'
    ? rawTab
    : (rawTab === 'admin' || rawTab === 'actividad') && canAdmin ? rawTab : 'dashboard';
  const setTab = (next: string, extra?: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value);
    setSearchParams(params, { replace: true });
  };

  const utils = trpc.useUtils();
  const [refreshing, setRefreshing] = useState(false);
  const dashboard = useTaDashboard(false, canView);
  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await utils.taMetrics.getDashboard.fetch({ refresh: true });
      utils.taMetrics.getDashboard.setData({ refresh: false }, fresh);
    } catch (e) {
      // RF-02: si la descarga falla se dice; el dashboard queda con lo anterior
      toast.error(t('talent_acquisition.refresh_failed', { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setRefreshing(false);
    }
  };

  const downloadedAt = dashboard.data?.downloadedAt
    ? new Date(dashboard.data.downloadedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })
    : null;

  // El intake vive en su propia entrada del menú: un HM sin acceso al dashboard y
  // los links viejos (?tab=intake / ?tab=solicitudes) van a su página.
  // El Interview Plan tiene su propia entrada en el menú (Sol 02/10): el link
  // viejo, que apuntaba a una pestaña de acá, va a esa pantalla.
  if (rawTab === 'entrevistas') return <Navigate to="/people/talent-acquisition/interview" replace />;

  if (!canView || rawTab === 'intake' || rawTab === 'solicitudes') {
    const params = new URLSearchParams(searchParams);
    if (rawTab === 'solicitudes') params.set('tab', 'solicitudes'); else params.delete('tab');
    const qs = params.toString();
    return <Navigate to={`/people/talent-acquisition/intake${qs ? `?${qs}` : ''}`} replace />;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-brand-green" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('talent_acquisition.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('talent_acquisition.subtitle')}</p>
          </div>
        </div>
        {tab === 'dashboard' && (
          <div className="flex items-center gap-3">
            {downloadedAt && (
              <span className="text-xs text-muted-foreground">
                {t('talent_acquisition.source_line', { rows: dashboard.data?.filas ?? 0, at: downloadedAt, today: dashboard.data?.hoy ?? '' })}
              </span>
            )}
            <Button size="sm" variant="outline" className="gap-1.5" onClick={refresh} disabled={refreshing || dashboard.isLoading}>
              {refreshing ? <WinclapLoader size={14} /> : <RefreshCw className="h-3.5 w-3.5" />}
              {t('talent_acquisition.refresh')}
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard">{t('talent_acquisition.tabs.dashboard')}</TabsTrigger>
          <TabsTrigger value="hiring">{t('talent_acquisition.hiring_plan.tab')}</TabsTrigger>
          <TabsTrigger value="asistente">{t('talent_acquisition.tabs.assistant')}</TabsTrigger>
          <TabsTrigger value="jds">{t('talent_acquisition.jd_library.tab')}</TabsTrigger>
          {canAdmin && <TabsTrigger value="actividad">{t('talent_acquisition.tabs.activity')}</TabsTrigger>}
          {canAdmin && <TabsTrigger value="admin">{t('talent_acquisition.tabs.admin')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="dashboard" className="mt-4">
          {dashboard.isLoading && <div className="flex items-center justify-center py-24"><WinclapLoader size={40} /></div>}
          {dashboard.error && (
            <Alert variant="destructive">
              <AlertTitle>{t('talent_acquisition.load_error_title')}</AlertTitle>
              <AlertDescription>{t('talent_acquisition.load_error_desc')} {dashboard.error.message}</AlertDescription>
            </Alert>
          )}
          {dashboard.data && <TaDashboard data={dashboard.data} />}
        </TabsContent>
        <TabsContent value="hiring" className="mt-4">
          {/* V1.3 §15: la Hiring Plan filtrable, solo lectura */}
          <TaHiringPlanTab />
        </TabsContent>
        <TabsContent value="jds" className="mt-4">
          {/* Biblioteca de JDs ya existentes, por puesto del mapa de comunidad (Sol 30/09) */}
          <TaJdLibraryTab />
        </TabsContent>
        <TabsContent value="asistente" className="mt-4">
          {/* Casa del Agente TA Metrics: el chat es el bot del costado (28/09) */}
          <TaAssistantTab canAdmin={canAdmin} onGoToBotAdmin={() => setTab('admin', { admin: 'bot' })} />
        </TabsContent>
        {canAdmin && (
          <TabsContent value="actividad" className="mt-4">
            <TaActivityTab />
          </TabsContent>
        )}
        {canAdmin && (
          <TabsContent value="admin" className="mt-4">
            {/* Toda la administración de TA: permisos, hiring managers, bot, reglas, fotos (28/09) */}
            <TaAdminTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
